import React, { useMemo, useState } from 'react';

import { Grid, Paper, TableBody, TableHead } from '@material-ui/core';
import { TableProps } from '@material-ui/core/Table/Table';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import { ValidatorForm } from 'react-material-ui-form-validator';

import { AutocompleteValidatorComponent } from 'components/FormComponents';
import { SearchComponent } from 'components/SearchComponent/SearchComponent';
import { TablePaginationComponent } from 'components/TablePaginationComponent/TablePaginationComponent';
import { FilterItem, TableData, TableParams } from 'types/other';

import { TableComponentRow } from './TableComponentRow/TableComponentRow';
import { TableHeaders } from './TableHeaders/TableHeaders';

import { Table } from './style';

export interface TableComponentProps extends TableProps {
  data: TableData;
  count: number;
  filterItems?: FilterItem[];
  filterLabel?: string;
  initialFilter?: any;
  enableSearch?: boolean;
  tableParams: TableParams;
  onParamsChange: (params: TableParams) => void;
}

export const TableComponent = ({
  data,
  count,
  filterItems,
  filterLabel,
  initialFilter,
  enableSearch,
  tableParams,
  onParamsChange,
  ...rest
}: TableComponentProps) => {
  const [search, setSearch] = useState('');

  const { headers, rows } = data;
  const { page, perPage, filterId, orderBy, orderDir } = tableParams;

  const hasActions = useMemo(() => !!rows.find(row => row.actions && row.actions.length > 0), [rows]);

  return (
    <Paper>
      <Table {...rest}>
        <TableHead>
          {(enableSearch || filterItems !== undefined) && (
            <TableRow>
              <TableCell colSpan={headers.length + 1} padding="none">
                {enableSearch && (
                  <SearchComponent
                    value={search}
                    setValue={setSearch}
                    getSearch={() => onParamsChange({ ...tableParams, search })}
                  />
                )}
                {filterItems && (
                  <ValidatorForm
                    className="filters-block"
                    onSubmit={() => {}}
                    style={{
                      padding: '0 10px',
                    }}
                  >
                    <Grid container>
                      <Grid item md={3}>
                        <AutocompleteValidatorComponent
                          value={filterId || initialFilter}
                          fieldName="id"
                          handleChange={(value: { id: number }) =>
                            onParamsChange({ ...tableParams, filterId: value.id })
                          }
                          selectList={filterItems}
                          itemsNamePath="value"
                          label={filterLabel}
                          disablePortal
                        />
                      </Grid>
                    </Grid>
                  </ValidatorForm>
                )}
              </TableCell>
            </TableRow>
          )}
          <TableHeaders
            columns={headers}
            withActions={hasActions}
            sortParams={{ orderBy, orderDir }}
            onChangeSort={({ orderBy, orderDir }) => onParamsChange({ ...tableParams, orderBy, orderDir })}
          />
        </TableHead>
        <TableBody>
          {rows.map(({ cells, onClick, actions, children }, index) => (
            <TableComponentRow key={index} data={cells} onRowClick={onClick} actions={actions} nestedRows={children} />
          ))}
        </TableBody>
      </Table>
      <TablePaginationComponent
        count={count}
        paginationParams={{ page, perPage }}
        onChangePaginationParams={onParamsChange}
      />
    </Paper>
  );
};
